import React from 'react';
import { useState } from 'react';
import { OneTimerTask } from '../types';
import { useMarkTaskDone, useUndoTask } from '../api/oneTimersApi';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import TaskCardMenu from './TaskCardMenu';
import EditTaskModal from './EditTaskModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface Props {
  task: OneTimerTask;
}

const OneTimerCard: React.FC<Props> = ({ task }) => {
  const { mutate: markDone, isPending } = useMarkTaskDone();
  const { mutate: undoTask, isPending: isUndoing } = useUndoTask();
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Determine deadline status for color-coding
  const getStatusColor = () => {
    if (!task.deadline) return 'blue';
    const dueDate = new Date(task.deadline);
    const today = new Date();
    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'red'; // overdue
    if (diffDays <= 3) return 'orange'; // due soon
    return 'blue';
  };

  const statusColor = getStatusColor();

  const borderColorClass = {
    blue: 'border-blue-400',
    orange: 'border-orange-400',
    red: 'border-red-500',
  }[statusColor];

  const badgeColorClass = {
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-yellow-100 text-yellow-700',
    red: 'bg-red-200 text-red-700',
  }[statusColor];

  const handleMarkDone = () => {
    if (task.isDone) return;
    markDone(task.id);
  };

  const handleUndo = () => {
    undoTask(task.id);
  };

  const renderDeadlineBadge = () => {
    if (!task.deadline) return null;
    const dueDate = new Date(task.deadline);
    const today = new Date();
    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const text = diffDays > 0 ? `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}` : diffDays === 0 ? 'Due today!' : `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`;
    return (
      <span className={clsx('text-xs px-2 py-1 rounded-full font-medium', badgeColorClass)}>{text}</span>
    );
  };

  const isNew = Date.now() - new Date(task.createdAt).getTime() < 5000;
  const newVariants = {
    initial: { backgroundColor: '#ecfdf5', scale: 1.05 },
    animate: { backgroundColor: '#ffffff', scale: 1, transition: { duration: 0.8 } },
  };

  return (
    <>
    <motion.div
      className={clsx(
        'rounded-lg p-6 shadow-sm transition-all bg-white border overflow-visible',
        borderColorClass,
        task.isDone ? 'opacity-70' : '',
        isPending || isUndoing ? 'opacity-40' : ''
      )}
      style={{ borderLeftWidth: 4 }}
      layout
      initial={isNew ? newVariants.initial : { opacity: 0, scale: 0.95 }}
      animate={isNew ? newVariants.animate : { opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox or Check Icon */}
        <div className="flex-shrink-0 mt-1">
          {!task.isDone ? (
            <input
              type="checkbox"
              onChange={handleMarkDone}
              disabled={isPending || isUndoing}
              className="h-5 w-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
          ) : (
            <div className="h-5 w-5 bg-green-500 rounded flex items-center justify-center">
              <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Title Row */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className={clsx(
              'text-lg font-semibold text-gray-900 leading-tight',
              task.isDone && 'line-through text-gray-500'
            )}>
              {task.title}
            </h3>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {renderDeadlineBadge()}
              <TaskCardMenu
                task={task}
                onEdit={() => setShowEditModal(true)}
                onDelete={() => setShowDeleteModal(true)}
                onUndo={task.isDone ? handleUndo : undefined}
              />
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <p className={clsx(
              'text-sm leading-relaxed mb-3',
              task.isDone ? 'text-gray-400' : 'text-gray-600'
            )}>
              {task.description}
            </p>
          )}

          {/* Metadata Footer */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Created {new Date(task.createdAt).toLocaleDateString()}</span>
            {task.isDone && task.completedAt && (
              <span className="flex items-center gap-1">
                <svg className="h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Completed {new Date(task.completedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>

    {/* Modals */}
    <EditTaskModal
      key={`edit-${task.id}`}
      isOpen={showEditModal}
      onClose={() => setShowEditModal(false)}
      task={task}
    />
    <DeleteConfirmationModal
      key={`delete-${task.id}`}
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      task={task}
    />
    </>
  );
};

export default OneTimerCard; 